import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';
import {
  clientPortalApi,
  type ModelCServiceStop,
  type ModelCServiceStopsResult,
  type RouteDirection,
  type RouteMetricsResult,
  type SyntaxMetricSlimKey,
} from './api';
import { PortalTopbar } from './PortalTopbar';
import { ProgrammingLegacyPane } from './ProgrammingLegacyPane';
import { BottomAnalyticsSheet, buildDetailRows, type AnalyticsTab } from './routeTerritorial/BottomAnalyticsSheet';
import { EntityPopupCard } from './routeTerritorial/EntityPopupCard';
import type { SelectedFeature } from './routeTerritorial/layers';
import { RightLayerDrawer, useLayerToggle } from './routeTerritorial/RightLayerDrawer';
import {
  computeAutoSpeedRange,
  extractBlocks,
  extractDayTypes,
  buildSpeedGradientStops,
  speedForZones,
  type SpeedBlockOption,
  type SpeedDayTypeOption,
} from './routeTerritorial/speedZones';
import { buildSyntaxGradientStops } from './routeTerritorial/syntaxZones';
import { TerritorialMap, type TerritorialMapMode } from './routeTerritorial/TerritorialMap';
import { CONNECTIVITY_METRICS, useConnectivityData } from './routeTerritorial/useConnectivityData';
import { useInfrastructureData } from './routeTerritorial/useInfrastructureData';
import { type LineContext, useLineGeometry } from './routeTerritorial/useLineGeometry';
import { useRouteTerritorialData } from './routeTerritorial/useRouteTerritorialData';
import { formatPct, formatPopulation } from './summaryFormat';
import './routeTerritorial/route-territorial.css';
import './line-operations.css';

type MainTab = 'servicio' | 'velocidades' | 'programacion' | 'cobertura' | 'infraestructura' | 'conectividad';

const WALK_SPEED_PRESETS: Array<{ value: number; label: string }> = [
  { value: 3, label: 'Caminata lenta' },
  { value: 4.5, label: 'Caminata normal' },
  { value: 6, label: 'Caminata rápida' },
  // "Bicicleta" es la MISMA red peatonal a mayor velocidad, no un grafo
  // ciclista específico (auditado: loadGraph() no filtra por access_mode) —
  // etiquetado como aproximación, nunca como isócrona ciclista real.
  { value: 15, label: 'Bicicleta (aprox.)' },
];

/**
 * `/analysis/:analysisId` — pantalla operativa única de la línea:
 * Recorrido | Velocidades | Programación | Cobertura, todas dentro del
 * mismo panel izquierdo flotante sobre el MISMO mapa dominante (nunca
 * remonta entre pestañas — ver TerritorialMap). Reemplaza tanto al iframe
 * legacy completo como a LineCoveragePage/LineViewerPage (ambos
 * consolidados acá). Programación sigue siendo el motor legacy
 * (`Eng.*`, sin tocar), embebido de forma aislada solo para esa pestaña.
 */
export function LineOperationsPage(): JSX.Element {
  const { slug } = usePortalAccess();
  const { analysisId } = useParams<{ analysisId: string }>();
  const token = useAuthStore(state => state.token);
  const geometry = useLineGeometry(slug, token ?? '', analysisId ?? '');

  if (!analysisId || geometry.loadState === 'error') {
    return (
      <div className="portal-denied">
        <h1>No se pudo cargar la línea</h1>
        <Link to="/analysis">Volver a Sistema de Transporte</Link>
      </div>
    );
  }

  if (geometry.loadState === 'loading' || !geometry.context) {
    return <div className="portal-loading">Cargando línea…</div>;
  }

  return (
    <LineOperationsContent
      slug={slug}
      token={token ?? ''}
      analysisId={analysisId}
      context={geometry.context}
      ida={geometry.ida}
      vuelta={geometry.vuelta}
      zonesIda={geometry.zonesIda}
      zonesVuelta={geometry.zonesVuelta}
      totalMIda={geometry.totalMIda}
      totalMVuelta={geometry.totalMVuelta}
    />
  );
}

function useSpeedCombos(slug: string, token: string, analysisId: string): { dayTypes: SpeedDayTypeOption[]; blocks: SpeedBlockOption[] } {
  const [dayTypes, setDayTypes] = useState<SpeedDayTypeOption[]>([]);
  const [blocks, setBlocks] = useState<SpeedBlockOption[]>([]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    clientPortalApi.getAnalysisAudit(slug, analysisId, token).then(audit => {
      if (cancelled) return;
      const combos = (audit as { combos?: unknown })?.combos;
      setDayTypes(extractDayTypes(combos));
      setBlocks(extractBlocks(combos));
    });
    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId]);
  return { dayTypes, blocks };
}

function useModelCServiceStops(args: {
  slug: string;
  token: string;
  analysisId: string;
  direction: RouteDirection;
}): { serviceStops: ModelCServiceStopsResult | null; serviceStopsError: string | null } {
  const { slug, token, analysisId, direction } = args;
  const [serviceStops, setServiceStops] = useState<ModelCServiceStopsResult | null>(null);
  const [serviceStopsError, setServiceStopsError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setServiceStops(null);
    setServiceStopsError(null);
    clientPortalApi
      .getModelCServiceStops(slug, analysisId, token, direction)
      .then(result => {
        if (!cancelled) setServiceStops(result);
      })
      .catch(() => {
        if (!cancelled) setServiceStopsError('No se pudo cargar la secuencia Model C de esta variante.');
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId, direction]);

  return { serviceStops, serviceStopsError };
}

function LineOperationsContent({
  slug,
  token,
  analysisId,
  context,
  ida,
  vuelta,
  zonesIda,
  zonesVuelta,
  totalMIda,
  totalMVuelta,
}: {
  slug: string;
  token: string;
  analysisId: string;
  context: LineContext;
  ida: [number, number][];
  vuelta: [number, number][];
  zonesIda: unknown[];
  zonesVuelta: unknown[];
  totalMIda: number;
  totalMVuelta: number;
}): JSX.Element {
  const isCircuit = context.routeMode === 'CIRCUIT';
  const [activeTab, setActiveTab] = useState<MainTab>('servicio');
  // Abierto por defecto — el usuario lo pidió explícitamente después de
  // encontrarlo colapsado al entrar a Cobertura; sigue siendo colapsable.
  const [rightDrawerOpen, setRightDrawerOpen] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState<AnalyticsTab>('poblacion');
  const [selected, setSelected] = useState<SelectedFeature | null>(null);
  const [selectedServiceStopId, setSelectedServiceStopId] = useState<string | null>(null);

  const [walkTimeMinutes, setWalkTimeMinutes] = useState<number>(5);
  const [walkingSpeedKmh, setWalkingSpeedKmh] = useState<number>(4.5);
  const [showCoverage, setShowCoverage] = useState(true);

  const { direction, setDirection, routeCoordinates, routeMetrics, coverageEntities, error } = useRouteTerritorialData({
    slug,
    token,
    analysisId,
    routeMode: context.routeMode,
    ida,
    vuelta,
    walkTimeMinutes,
    walkingSpeedKmh,
  });
  const { activeLayers, toggleLayer, showAll, hideAll } = useLayerToggle();
  const { serviceStops, serviceStopsError } = useModelCServiceStops({ slug, token, analysisId, direction });
  const selectedServiceStop =
    serviceStops?.stops.find(stop => stop.facilityId === selectedServiceStopId) ?? serviceStops?.stops[0] ?? null;
  useEffect(() => {
    setSelectedServiceStopId(null);
  }, [analysisId, direction]);

  const { dayTypes, blocks } = useSpeedCombos(slug, token, analysisId);
  const [selectedDayType, setSelectedDayType] = useState<string>('');
  const [selectedBlockKey, setSelectedBlockKey] = useState<string>('');
  useEffect(() => {
    if (!selectedDayType && dayTypes.length > 0) setSelectedDayType(dayTypes[0].dayType);
  }, [dayTypes, selectedDayType]);
  useEffect(() => {
    if (!selectedBlockKey && blocks.length > 0) setSelectedBlockKey(blocks[0].blockKey);
  }, [blocks, selectedBlockKey]);
  const selectedCalendarId = dayTypes.find(d => d.dayType === selectedDayType)?.calendarId ?? '';

  // Rango de color editable — restaurado a pedido del usuario (existía en
  // el visor legacy: min/max + botón "Automático", se había perdido en el
  // port a React). `legendAuto=true` recalcula el rango a partir de las
  // velocidades reales del combo actual cada vez que cambia; tocar los
  // inputs manualmente lo desactiva, igual que `#legend-min`/`#legend-max`
  // legacy.
  const [legendAuto, setLegendAuto] = useState(true);
  const [legendMin, setLegendMin] = useState(10);
  const [legendMax, setLegendMax] = useState(40);
  const autoRange = useMemo(
    () => computeAutoSpeedRange(zonesIda, selectedCalendarId, selectedBlockKey),
    [zonesIda, selectedCalendarId, selectedBlockKey]
  );
  useEffect(() => {
    if (legendAuto) {
      setLegendMin(autoRange.min);
      setLegendMax(autoRange.max);
    }
  }, [legendAuto, autoRange]);

  const speedStops = useMemo(() => {
    if (activeTab !== 'velocidades' || !selectedCalendarId || !selectedBlockKey) return null;
    const range = { min: legendMin, max: legendMax };
    return {
      IDA: buildSpeedGradientStops(zonesIda, totalMIda, selectedCalendarId, selectedBlockKey, range),
      VUELTA: isCircuit ? [] : buildSpeedGradientStops(zonesVuelta, totalMVuelta, selectedCalendarId, selectedBlockKey, range),
    };
  }, [activeTab, selectedCalendarId, selectedBlockKey, zonesIda, zonesVuelta, totalMIda, totalMVuelta, isCircuit, legendMin, legendMax]);

  const avgSpeedIda = selectedCalendarId && selectedBlockKey ? speedForZones(zonesIda, selectedCalendarId, selectedBlockKey) : null;
  const avgSpeedVuelta =
    !isCircuit && selectedCalendarId && selectedBlockKey ? speedForZones(zonesVuelta, selectedCalendarId, selectedBlockKey) : null;

  const detailRows = useMemo(() => buildDetailRows(routeMetrics, coverageEntities), [routeMetrics, coverageEntities]);

  const { pavementStops, infraRouteMetrics } = useInfrastructureData(slug, token, analysisId, isCircuit);
  const [infraDrawerOpen, setInfraDrawerOpen] = useState(true);
  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(true);

  // Conectividad — sintaxis espacial es una propiedad de RED, no del
  // recorrido: a diferencia de velocidad, el rango "Automático" usa el
  // min/max de TODA la red (no solo los tramos de esta línea), para que
  // la misma calle se vea del mismo color sin importar qué línea la
  // incluya.
  const [connMetric, setConnMetric] = useState<SyntaxMetricSlimKey>('integrationGlobal');
  const [connLegendAuto, setConnLegendAuto] = useState(true);
  const [connLegendMin, setConnLegendMin] = useState(0);
  const [connLegendMax, setConnLegendMax] = useState(1);
  const { ida: connIda, vuelta: connVuelta, valueByEdge: connValueByEdge, range: connRange, hasMetrics: connHasMetrics, loadState: connLoadState } =
    useConnectivityData(slug, token, analysisId, isCircuit, connMetric);
  useEffect(() => {
    if (connLegendAuto && connRange) {
      setConnLegendMin(connRange[connMetric].min);
      setConnLegendMax(connRange[connMetric].max);
    }
  }, [connLegendAuto, connRange, connMetric]);
  const syntaxStops = useMemo(() => {
    if (activeTab !== 'conectividad') return null;
    const range = { min: connLegendMin, max: connLegendMax };
    return {
      IDA: connIda ? buildSyntaxGradientStops(connIda.pavement.segments, connIda.pavement.totalLengthM, connValueByEdge, range) : [],
      VUELTA: connVuelta ? buildSyntaxGradientStops(connVuelta.pavement.segments, connVuelta.pavement.totalLengthM, connValueByEdge, range) : [],
    };
  }, [activeTab, connIda, connVuelta, connValueByEdge, connLegendMin, connLegendMax]);

  const mapMode: TerritorialMapMode =
    activeTab === 'cobertura'
      ? 'coverage'
      : activeTab === 'velocidades'
        ? 'speed'
        : activeTab === 'infraestructura'
          ? 'infrastructure'
          : activeTab === 'conectividad'
            ? 'connectivity'
            : 'overview';

  function selectEntity(f: SelectedFeature): void {
    setSelected(f);
  }

  return (
    <div className="lop-page">
      <PortalTopbar />
      <div className="lop-topbar">
        <span className="lop-topbar-title">{context.label}</span>
        <div className="lop-topbar-links">
          <Link to={`/analysis/${analysisId}/ficha`} target="_blank">
            Ficha
          </Link>
          <Link to="/analysis">Sistema de Transporte</Link>
        </div>
      </div>

      <div className="lop-map-area">
        <TerritorialMap
          key={analysisId}
          mode={mapMode}
          ida={ida}
          vuelta={vuelta}
          routeCoordinates={routeCoordinates}
          direction={direction}
          isCircuit={isCircuit}
          coveragePolygon={coverageEntities?.coveragePolygon ?? null}
          showCoverage={showCoverage}
          showPois={activeTab === 'cobertura' || activeTab === 'infraestructura'}
          layerScope={activeTab === 'infraestructura' ? 'infraestructura' : 'cobertura'}
          routeMetrics={activeTab === 'infraestructura' ? infraRouteMetrics : routeMetrics}
          coverageEntities={coverageEntities}
          activeLayers={activeLayers}
          speedStops={speedStops}
          pavementStops={activeTab === 'infraestructura' ? pavementStops : null}
          syntaxStops={syntaxStops}
          serviceStops={activeTab === 'servicio' ? serviceStops?.stops ?? [] : []}
          selectedServiceStopId={activeTab === 'servicio' ? selectedServiceStopId : null}
          onSelectServiceStop={stop => {
            setSelected(null);
            setSelectedServiceStopId(stop.facilityId);
          }}
          onSelectFeature={selectEntity}
        />

        <div className="lop-left-panel">
          <div className="lop-left-header">
            <p className="lop-line-label">{context.label}</p>
            <div className="lop-main-tabs lop-main-tabs-6">
              {(
                [
                  ['servicio', 'SERVICIO'],
                  ['velocidades', 'VELOCIDADES'],
                  ['conectividad', 'CONECTIVIDAD'],
                  ['infraestructura', 'INFRAESTRUCTURA'],
                  ['programacion', 'PROGRAMACIÓN'],
                  ['cobertura', 'COBERTURA'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  className={activeTab === key ? 'lop-main-tab lop-main-tab-active' : 'lop-main-tab'}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="lop-tab-content">
            {activeTab === 'servicio' && (
              <ServicioTabBody
                isCircuit={isCircuit}
                direction={direction}
                setDirection={setDirection}
                serviceStops={serviceStops}
                error={serviceStopsError}
                selectedStop={selectedServiceStop}
              />
            )}

            {activeTab === 'velocidades' && (
              <VelocidadesTabBody
                dayTypes={dayTypes}
                blocks={blocks}
                selectedDayType={selectedDayType}
                selectedBlockKey={selectedBlockKey}
                onDayTypeChange={setSelectedDayType}
                onBlockChange={setSelectedBlockKey}
                isCircuit={isCircuit}
                avgSpeedIda={avgSpeedIda}
                avgSpeedVuelta={avgSpeedVuelta}
                legendMin={legendMin}
                legendMax={legendMax}
                legendAuto={legendAuto}
                onLegendMinChange={v => {
                  setLegendAuto(false);
                  setLegendMin(v);
                }}
                onLegendMaxChange={v => {
                  setLegendAuto(false);
                  setLegendMax(v);
                }}
                onLegendAuto={() => setLegendAuto(true)}
              />
            )}

            {activeTab === 'conectividad' && (
              <ConectividadTabBody
                metric={connMetric}
                onMetricChange={setConnMetric}
                legendMin={connLegendMin}
                legendMax={connLegendMax}
                legendAuto={connLegendAuto}
                onLegendMinChange={v => {
                  setConnLegendAuto(false);
                  setConnLegendMin(v);
                }}
                onLegendMaxChange={v => {
                  setConnLegendAuto(false);
                  setConnLegendMax(v);
                }}
                onLegendAuto={() => setConnLegendAuto(true)}
                loadState={connLoadState}
                hasMetrics={connHasMetrics}
              />
            )}

            {activeTab === 'infraestructura' && <InfraestructuraTabBody metrics={infraRouteMetrics} />}

            {activeTab === 'programacion' && (
              <div className="lop-programming-pane">
                <ProgrammingLegacyPane analysisId={analysisId} label={context.label} />
              </div>
            )}

            {activeTab === 'cobertura' && (
              <CoberturaTabBody
                isCircuit={isCircuit}
                direction={direction}
                setDirection={setDirection}
                error={error}
                walkTimeMinutes={walkTimeMinutes}
                setWalkTimeMinutes={setWalkTimeMinutes}
                walkingSpeedKmh={walkingSpeedKmh}
                setWalkingSpeedKmh={setWalkingSpeedKmh}
                showCoverage={showCoverage}
                setShowCoverage={setShowCoverage}
                populationCovered={coverageEntities?.populationCovered}
                populationCoveragePct={coverageEntities?.populationCoveragePct ?? null}
              />
            )}
          </div>
        </div>

        {activeTab === 'cobertura' && (
          <RightLayerDrawer
            open={rightDrawerOpen}
            onToggleOpen={() => setRightDrawerOpen(o => !o)}
            title="Capas territoriales"
            tabFilter="cobertura"
            routeMetrics={routeMetrics}
            coverageEntities={coverageEntities}
            activeLayers={activeLayers}
            onToggleLayer={toggleLayer}
            onShowAll={showAll}
            onHideAll={hideAll}
          />
        )}

        {activeTab === 'infraestructura' && (
          <RightLayerDrawer
            open={infraDrawerOpen}
            onToggleOpen={() => setInfraDrawerOpen(o => !o)}
            title="Elementos viales"
            tabFilter="infraestructura"
            routeMetrics={infraRouteMetrics}
            coverageEntities={null}
            activeLayers={activeLayers}
            onToggleLayer={toggleLayer}
            onShowAll={showAll}
            onHideAll={hideAll}
          />
        )}

        {activeTab === 'servicio' && (
          <ServiceStopsDrawer
            open={serviceDrawerOpen}
            onToggleOpen={() => setServiceDrawerOpen(o => !o)}
            direction={direction}
            result={serviceStops}
            selectedStopId={selectedServiceStopId}
            onSelectStop={stop => {
              setSelected(null);
              setSelectedServiceStopId(stop.facilityId);
            }}
          />
        )}

        {selected && (
          <div className="lop-entity-bar">
            <EntityPopupCard feature={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>

      {activeTab === 'cobertura' && (
        <BottomAnalyticsSheet
          open={sheetOpen}
          onToggleOpen={() => setSheetOpen(o => !o)}
          activeTab={analyticsTab}
          onTabChange={setAnalyticsTab}
          coverageEntities={coverageEntities}
          detailRows={detailRows}
          onSelect={selectEntity}
        />
      )}
    </div>
  );
}

function formatServiceStopName(stop: ModelCServiceStop): string {
  return stop.displayName || stop.streetName || `Parada ${stop.sequence}`;
}

function formatStopOrdinal(sequence: number): string {
  return String(sequence).padStart(2, '0');
}

function formatMeters(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString('es-AR')} m`;
}

function ServicioTabBody({
  isCircuit,
  direction,
  setDirection,
  serviceStops,
  error,
  selectedStop,
}: {
  isCircuit: boolean;
  direction: RouteDirection;
  setDirection: (d: RouteDirection) => void;
  serviceStops: ModelCServiceStopsResult | null;
  error: string | null;
  selectedStop: ModelCServiceStop | null;
}): JSX.Element {
  return (
    <div>
      <div className="lop-block">
        <p className="lop-block-title">Variante</p>
        {isCircuit ? (
          <div className="ft-direction-tabs">
            <button className="ft-tab ft-tab-active" disabled>
              CIRCUITO
            </button>
          </div>
        ) : (
          <div className="ft-direction-tabs">
            <button className={direction === 'IDA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('IDA')}>
              IDA
            </button>
            <button className={direction === 'VUELTA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('VUELTA')}>
              VUELTA
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="lop-block">
          <p className="ft-error">{error}</p>
        </div>
      )}

      <div className="lop-block">
        <p className="lop-block-title">Resumen</p>
        {serviceStops ? (
          <div className="ft-kpi-row">
            <div className="ft-kpi">
              <span className="ft-kpi-value">{serviceStops.summary.stopCount}</span>
              <span className="ft-kpi-label">Paradas</span>
            </div>
            <div className="ft-kpi">
              <span className="ft-kpi-value">{formatMeters(serviceStops.summary.averageSpacingM)}</span>
              <span className="ft-kpi-label">Espaciamiento medio</span>
            </div>
            <div className="ft-kpi">
              <span className="ft-kpi-value">{formatMeters(serviceStops.summary.minSpacingM)}</span>
              <span className="ft-kpi-label">Mínimo</span>
            </div>
            <div className="ft-kpi">
              <span className="ft-kpi-value">{formatMeters(serviceStops.summary.maxSpacingM)}</span>
              <span className="ft-kpi-label">Máximo</span>
            </div>
          </div>
        ) : (
          <p className="cov-analytics-empty">Cargando paradas…</p>
        )}
      </div>

      {selectedStop && (
        <div className="lop-block">
          <p className="lop-block-title">Detalle de parada</p>
          <p className="lop-stop-detail-title">{formatServiceStopName(selectedStop)}</p>
          {selectedStop.displayName && (
            <p className="lop-stop-detail-subtitle">
              Parada {selectedStop.sequence} · {direction}
              <br />
              Anterior {formatMeters(selectedStop.distanceFromPreviousM)} · Siguiente {formatMeters(selectedStop.distanceToNextM)}
            </p>
          )}
          <div className="lop-stop-detail-grid">
            {!selectedStop.displayName && (
              <>
                <span>Secuencia</span>
                <strong>{formatStopOrdinal(selectedStop.sequence)}</strong>
                <span>Variante</span>
                <strong>{direction}</strong>
                <span>Desde anterior</span>
                <strong>{formatMeters(selectedStop.distanceFromPreviousM)}</strong>
                <span>A siguiente</span>
                <strong>{formatMeters(selectedStop.distanceToNextM)}</strong>
              </>
            )}
            <span>Rutas compartidas</span>
            <strong>{selectedStop.sharedRoutesCount ?? '—'}</strong>
            {selectedStop.virtualStopArea && (
              <>
                <span>VSA</span>
                <strong>Sí</strong>
              </>
            )}
            {selectedStop.complexStopZone && (
              <>
                <span>Zona compleja</span>
                <strong>Sí</strong>
              </>
            )}
          </div>
        </div>
      )}

      <p className="ft-note">Secuencia canónica Model C. No usa paradas municipales ni inventa nombres de calle.</p>
    </div>
  );
}

function ServiceStopsDrawer({
  open,
  onToggleOpen,
  direction,
  result,
  selectedStopId,
  onSelectStop,
}: {
  open: boolean;
  onToggleOpen: () => void;
  direction: RouteDirection;
  result: ModelCServiceStopsResult | null;
  selectedStopId: string | null;
  onSelectStop: (stop: ModelCServiceStop) => void;
}): JSX.Element {
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!selectedStopId) return;
    rowRefs.current[selectedStopId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedStopId]);

  return (
    <div className={open ? 'lop-right-drawer lop-right-drawer-open lop-service-drawer' : 'lop-right-drawer lop-service-drawer'}>
      <button className="lop-right-drawer-handle" onClick={onToggleOpen} aria-label={open ? 'Ocultar paradas' : 'Mostrar paradas'} title="Paradas">
        {open ? '✕' : '▤'}
      </button>
      {open && (
        <div className="lop-right-drawer-body">
          <div className="lop-right-drawer-header">
            <p className="cov-panel-title">Paradas · {direction}</p>
          </div>
          {!result ? (
            <p className="cov-analytics-empty">Cargando paradas…</p>
          ) : (
            <div className="lop-stop-list">
              {result.stops.map(stop => (
                <div key={stop.facilityId} className="lop-stop-list-item">
                  <button
                    ref={node => {
                      rowRefs.current[stop.facilityId] = node;
                    }}
                    className={selectedStopId === stop.facilityId ? 'lop-stop-row lop-stop-row-active' : 'lop-stop-row'}
                    onClick={() => onSelectStop(stop)}
                  >
                    <span className="lop-stop-ordinal">{formatStopOrdinal(stop.sequence)}</span>
                    <span className="lop-stop-name">{formatServiceStopName(stop)}</span>
                  </button>
                  {stop.distanceToNextM !== null && (
                    <div className="lop-stop-gap" aria-label={`Distancia a siguiente parada: ${formatMeters(stop.distanceToNextM)}`}>
                      <span>↓</span>
                      <strong>{formatMeters(stop.distanceToNextM)}</strong>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PAVEMENT_BUCKET_COLORS: Record<string, string> = {
  ASPHALT: '#546e7a',
  OTHER_PAVED: '#8d9ba3',
  UNPAVED: '#b08968',
  UNKNOWN: '#3a4a5a',
};
const PAVEMENT_BUCKET_LABELS: Record<string, string> = {
  ASPHALT: 'Asfalto',
  OTHER_PAVED: 'Otros pavimentos',
  UNPAVED: 'No pavimentado',
  UNKNOWN: 'Sin dato',
};

/**
 * Infraestructura vial — pavimento por tramo (color en el mapa) +
 * semáforos/reductores/cruces, que se movieron acá desde Cobertura a
 * pedido del usuario ("obviamente no se ven más en cobertura"). El
 * detalle de capas (checkbox + contador) vive en el cajón derecho, igual
 * que Cobertura; acá solo va la leyenda de pavimento y los totales.
 */
function InfraestructuraTabBody({ metrics }: { metrics: RouteMetricsResult | null }): JSX.Element {
  if (!metrics) return <p className="cov-analytics-empty">Calculando…</p>;
  return (
    <div>
      <div className="lop-block">
        <p className="lop-block-title">Pavimento (color en el mapa)</p>
        <div className="lop-pavement-bar">
          {metrics.pavement.breakdown
            .filter(b => b.pct > 0)
            .map(b => (
              <div key={b.bucket} className="lop-pavement-seg" style={{ width: `${b.pct}%`, background: PAVEMENT_BUCKET_COLORS[b.bucket] }} title={`${PAVEMENT_BUCKET_LABELS[b.bucket]} — ${b.pct}%`} />
            ))}
        </div>
        <div className="lop-pavement-legend">
          {metrics.pavement.breakdown
            .filter(b => b.pct > 0)
            .map(b => (
              <span key={b.bucket} className="lop-pavement-legend-item">
                <span className="lop-pavement-swatch" style={{ background: PAVEMENT_BUCKET_COLORS[b.bucket] }} />
                {PAVEMENT_BUCKET_LABELS[b.bucket]} {b.pct}%
              </span>
            ))}
        </div>
      </div>
      <div className="lop-block">
        <p className="lop-block-title">Elementos sobre el recorrido</p>
        <div className="ft-kpi-row">
          <div className="ft-kpi">
            <span className="ft-kpi-value">{metrics.trafficSignals.count}</span>
            <span className="ft-kpi-label">Semáforos</span>
          </div>
          <div className="ft-kpi">
            <span className="ft-kpi-value">{metrics.trafficCalming.count}</span>
            <span className="ft-kpi-label">Reductores</span>
          </div>
          <div className="ft-kpi">
            <span className="ft-kpi-value">{metrics.crossings.count}</span>
            <span className="ft-kpi-label">Cruces peatonales</span>
          </div>
        </div>
      </div>
      <p className="ft-note">
        Pavimento por longitud real de cada tramo (asfalto/otros pavimentos/no pavimentado/sin dato). Semáforos,
        reductores y cruces peatonales — activables en el panel derecho, con su ubicación exacta en el mapa.
      </p>
    </div>
  );
}

/**
 * Conectividad (sintaxis espacial) — colorea el recorrido según la
 * métrica de sintaxis espacial seleccionada, mismo patrón visual que
 * Velocidades (gradiente + rango editable + botón Automático), pero el
 * rango "Automático" usa el min/max de TODA la red (`connRange`, ver
 * `useConnectivityData`), no solo los tramos de esta línea — sintaxis
 * espacial es una propiedad de red, no del recorrido.
 */
function ConectividadTabBody({
  metric,
  onMetricChange,
  legendMin,
  legendMax,
  legendAuto,
  onLegendMinChange,
  onLegendMaxChange,
  onLegendAuto,
  loadState,
  hasMetrics,
}: {
  metric: SyntaxMetricSlimKey;
  onMetricChange: (v: SyntaxMetricSlimKey) => void;
  legendMin: number;
  legendMax: number;
  legendAuto: boolean;
  onLegendMinChange: (v: number) => void;
  onLegendMaxChange: (v: number) => void;
  onLegendAuto: () => void;
  loadState: 'loading' | 'error' | 'ready';
  hasMetrics: boolean;
}): JSX.Element {
  if (loadState === 'error' || (loadState === 'ready' && !hasMetrics)) {
    return (
      <div>
        <div className="lop-block">
          <p className="lop-block-title">Sintaxis espacial</p>
          <p className="cov-analytics-empty" style={{ marginBottom: 0 }}>
            {loadState === 'error'
              ? 'No se pudo cargar la sintaxis espacial para esta línea.'
              : 'El motor de sintaxis espacial existe y está integrado, pero todavía no hay un cómputo completado para Bahía Blanca. No se muestra un color aproximado ni un dato de otra red.'}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="lop-block">
        <p className="lop-block-title">Métrica</p>
        <select value={metric} onChange={e => onMetricChange(e.target.value as SyntaxMetricSlimKey)}>
          {CONNECTIVITY_METRICS.map(m => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className="lop-block">
        <p className="lop-block-title">{CONNECTIVITY_METRICS.find(m => m.key === metric)?.label}</p>
        <div className="lop-syntax-gradient-legend" />
        <div className="lop-speed-gradient-labels">
          <span>{legendMin}</span>
          <span>{legendMax}</span>
        </div>
        <div className="lop-field-row" style={{ marginTop: 8 }}>
          <div>
            <label className="lop-field-label">Mín</label>
            <input type="number" step={0.01} value={legendMin} onChange={e => onLegendMinChange(Number(e.target.value))} />
          </div>
          <div>
            <label className="lop-field-label">Máx</label>
            <input type="number" step={0.01} value={legendMax} onChange={e => onLegendMaxChange(Number(e.target.value))} />
          </div>
        </div>
        <button className={legendAuto ? 'lop-mode-btn lop-mode-btn-active' : 'lop-mode-btn'} style={{ width: '100%', marginTop: 6 }} onClick={onLegendAuto}>
          Automático
        </button>
      </div>
      <p className="ft-note">
        Sintaxis espacial (Angular Segment Analysis) sobre la red completa de Bahía Blanca — el color de cada tramo
        no depende de esta línea, es una propiedad de la calle en toda la red. "Automático" usa el rango real de la
        red completa para la métrica elegida.
      </p>
    </div>
  );
}

function VelocidadesTabBody({
  dayTypes,
  blocks,
  selectedDayType,
  selectedBlockKey,
  onDayTypeChange,
  onBlockChange,
  isCircuit,
  avgSpeedIda,
  avgSpeedVuelta,
  legendMin,
  legendMax,
  legendAuto,
  onLegendMinChange,
  onLegendMaxChange,
  onLegendAuto,
}: {
  dayTypes: SpeedDayTypeOption[];
  blocks: SpeedBlockOption[];
  selectedDayType: string;
  selectedBlockKey: string;
  onDayTypeChange: (v: string) => void;
  onBlockChange: (v: string) => void;
  isCircuit: boolean;
  avgSpeedIda: number | null;
  avgSpeedVuelta: number | null;
  legendMin: number;
  legendMax: number;
  legendAuto: boolean;
  onLegendMinChange: (v: number) => void;
  onLegendMaxChange: (v: number) => void;
  onLegendAuto: () => void;
}): JSX.Element {
  const block = blocks.find(b => b.blockKey === selectedBlockKey);
  return (
    <div>
      <div className="lop-block">
        <p className="lop-block-title">Día tipo y bloque horario</p>
        <div className="lop-select-row">
          <label>Día tipo</label>
          <select value={selectedDayType} onChange={e => onDayTypeChange(e.target.value)}>
            {dayTypes.map(d => (
              <option key={d.dayType} value={d.dayType}>
                {d.dayType}
              </option>
            ))}
          </select>
        </div>
        <div className="lop-select-row">
          <label>Bloque horario</label>
          <select value={selectedBlockKey} onChange={e => onBlockChange(e.target.value)}>
            {blocks.map(b => (
              <option key={b.blockKey} value={b.blockKey}>
                {b.label} ({b.startTime}–{b.endTime})
              </option>
            ))}
          </select>
        </div>
        {block && (
          <div className="ft-kpi-row">
            <div className="ft-kpi">
              <span className="ft-kpi-value">{avgSpeedIda !== null ? `${avgSpeedIda} km/h` : '—'}</span>
              <span className="ft-kpi-label">{isCircuit ? 'Velocidad media' : 'IDA — velocidad media'}</span>
            </div>
            {!isCircuit && (
              <div className="ft-kpi">
                <span className="ft-kpi-value">{avgSpeedVuelta !== null ? `${avgSpeedVuelta} km/h` : '—'}</span>
                <span className="ft-kpi-label">VUELTA — velocidad media</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="lop-block">
        <p className="lop-block-title">Velocidad (km/h)</p>
        <div className="lop-speed-gradient-legend" />
        <div className="lop-speed-gradient-labels">
          <span>{legendMin}</span>
          <span>{legendMax}</span>
        </div>
        <div className="lop-field-row" style={{ marginTop: 8 }}>
          <div>
            <label className="lop-field-label">Mín (km/h)</label>
            <input type="number" step={1} value={legendMin} onChange={e => onLegendMinChange(Number(e.target.value))} />
          </div>
          <div>
            <label className="lop-field-label">Máx (km/h)</label>
            <input type="number" step={1} value={legendMax} onChange={e => onLegendMaxChange(Number(e.target.value))} />
          </div>
        </div>
        <button className={legendAuto ? 'lop-mode-btn lop-mode-btn-active' : 'lop-mode-btn'} style={{ width: '100%', marginTop: 6 }} onClick={onLegendAuto}>
          Automático
        </button>
      </div>
      <p className="ft-note">
        Velocidades provienen de observaciones GPS municipales cargadas en Transit Suite, completadas con inferencia
        documentada donde falta dato directo — mismo dato que usa Programación.
      </p>
    </div>
  );
}

function CoberturaTabBody({
  isCircuit,
  direction,
  setDirection,
  error,
  walkTimeMinutes,
  setWalkTimeMinutes,
  walkingSpeedKmh,
  setWalkingSpeedKmh,
  showCoverage,
  setShowCoverage,
  populationCovered,
  populationCoveragePct,
}: {
  isCircuit: boolean;
  direction: string;
  setDirection: (d: 'IDA' | 'VUELTA' | 'CIRCUITO') => void;
  error: string | null;
  walkTimeMinutes: number;
  setWalkTimeMinutes: (v: number) => void;
  walkingSpeedKmh: number;
  setWalkingSpeedKmh: (v: number) => void;
  showCoverage: boolean;
  setShowCoverage: (v: boolean) => void;
  populationCovered: number | undefined;
  populationCoveragePct: number | null;
}): JSX.Element {
  return (
    <div>
      <div className="lop-block">
        <p className="lop-block-title">Método: Corredor</p>
        <p className="ft-note" style={{ marginTop: 0 }}>
          Sin paradas validadas para esta línea — accesos: nodos reales de la red con conectividad ≥3, incluye
          cabeceras.
        </p>
      </div>

      <div className="lop-block">
        <p className="lop-block-title">Variante</p>
        <div className="ft-direction-tabs">
          {isCircuit ? (
            <button className="ft-tab ft-tab-active" disabled>
              CIRCUITO
            </button>
          ) : (
            <>
              <button className={direction === 'IDA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('IDA')}>
                IDA
              </button>
              <button className={direction === 'VUELTA' ? 'ft-tab ft-tab-active' : 'ft-tab'} onClick={() => setDirection('VUELTA')}>
                VUELTA
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="lop-block">
          <p className="ft-error">{error}</p>
        </div>
      )}

      <div className="lop-block">
        <p className="lop-block-title">Cobertura peatonal</p>
        <label className="cov-checkbox-row">
          <input type="checkbox" checked={showCoverage} onChange={e => setShowCoverage(e.target.checked)} />
          Mostrar isócrona en el mapa
        </label>
        {/* Campos libres, no un <select> de presets fijos — mismo criterio
            que ya tenía el visor legacy ("Configurador flexible"): tiempo y
            velocidad son cualquier número, los botones de modo solo
            precargan un valor de referencia, nunca restringen la edición
            manual (regresión encontrada por el usuario en QA: había quedado
            como un <select> rígido de 4 opciones). */}
        <div className="lop-field-row" style={{ marginTop: 8 }}>
          <div>
            <label className="lop-field-label">Tiempo (min)</label>
            <input
              type="number"
              min={1}
              max={30}
              step={1}
              value={walkTimeMinutes}
              onChange={e => setWalkTimeMinutes(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="lop-field-label">Velocidad (km/h)</label>
            <input
              type="number"
              min={2}
              max={20}
              step={0.5}
              value={walkingSpeedKmh}
              onChange={e => setWalkingSpeedKmh(Number(e.target.value))}
            />
          </div>
        </div>
        <p className="lop-field-label" style={{ marginTop: 8 }}>
          Modo
        </p>
        <div className="lop-mode-btn-row">
          {WALK_SPEED_PRESETS.map(p => (
            <button
              key={p.value}
              className={walkingSpeedKmh === p.value ? 'lop-mode-btn lop-mode-btn-active' : 'lop-mode-btn'}
              onClick={() => setWalkingSpeedKmh(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {!WALK_SPEED_PRESETS.some(p => p.value === walkingSpeedKmh) && (
          <p className="ft-note" style={{ marginTop: 4 }}>
            Personalizado — {walkingSpeedKmh} km/h
          </p>
        )}
      </div>

      <div className="lop-block">
        <p className="lop-block-title">Población cubierta</p>
        <div className="ft-kpi-row">
          <div className="ft-kpi">
            <span className="ft-kpi-value">{populationCovered !== undefined ? formatPopulation(populationCovered) : '—'}</span>
            <span className="ft-kpi-label">Habitantes</span>
          </div>
          <div className="ft-kpi">
            <span className="ft-kpi-value">{formatPct(populationCoveragePct)}</span>
            <span className="ft-kpi-label">% del proyecto</span>
          </div>
        </div>
      </div>

      <p className="ft-note">Capas de entidades e inventario urbano: panel derecho. Análisis detallado: abrir la analítica en el borde inferior del mapa.</p>
    </div>
  );
}
